/* !
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import {
	AbstractControl,
	UntypedFormControl
} from '@angular/forms';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	PaymentAllocationsComponent
} from '@insurance/components/payment-allocations/payment-allocations.component';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	InsuranceService
} from '@insurance/services/insurance.service';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	SiteLayoutService
} from '@shared/services/site-layout.service';
import {
	delay,
	from,
	map,
	Observable,
	of,
	Subject,
	switchMap,
	takeUntil
} from 'rxjs';

/* eslint-enable max-len */

@Component({
	selector: 'commission-payment-transaction',
	templateUrl: './commission-payment-transaction.component.html'
})

/**
 * A component representing a wizard step to collect the commission payment
 * transaction details.
 *
 * @export
 * @class CommissionPaymentTransactionComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class CommissionPaymentTransactionComponent
implements OnInit,
	OnDestroy,
	IDynamicComponent<DynamicWizardComponent, IWizardContext>
{

	/**
	 * Initializes an instance of the commission payment transaction component.
	 *
	 * @param {SiteLayoutService} siteLayoutService
	 * The site layout service used in this component.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public constructor(
		public siteLayoutService: SiteLayoutService,
		public insuranceService: InsuranceService) {
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<
	 * 	DynamicWizardComponent,
	 * 	IWizardContext>}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	@Input() public context: IDynamicComponentContext<
		DynamicWizardComponent,
		IWizardContext>;

	/**
	 * Gets or sets the payment allocations child component reference.
	 *
	 * @type {PaymentAllocationsComponent}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	@ViewChild('paymentAllocations')
	public paymentAllocations: PaymentAllocationsComponent;

	/**
	 * Gets or sets the payment allocation settings retrieved from
	 * the product associated to the policy term.
	 *
	 * @type {any}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public paymentAllocationSettings: any;

	/**
	 * Gets or sets the formly layout used in implementing components.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public dynamicFormlyLayout: FormlyFieldConfig[];

	/**
	 * Gets or sets the transaction entity instance that
	 * is associated with the commission payment.
	 *
	 * @type {IEntityInstance}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public transaction: IEntityInstance;

	/**
	 * Gets or sets the list of commission transactions
	 * associated to the current transaction.
	 *
	 * @type {any[]}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public commissionTransactions: any[];

	/**
	 * Gets or sets the context active menu item current data collected on the
	 * dynamic wizard steps.
	 *
	 * @type {any}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public get currentData(): any
	{
		return this.context.source.activeMenuItem.currentData.data;
	}

	/**
	 * Gets the agency ID from the current context data.
	 *
	 * @type {number}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public get agencyId(): number
	{
		return Number(this.currentData.id);
	}

	/**
	 * Gets a value indicating whether payment allocations are enabled.
	 *
	 * @type {boolean}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public get paymentAllocationsEnabled(): boolean
	{
		return this.paymentAllocationSettings?.enabled ?? false;
	}

	/**
	 * Gets the commission transaction field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public get commissionTransactionFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout[2];
	}

	/**
	 * Gets the amount field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public get amountFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout[3];
	}

	/**
	 * Gets the transaction amount field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public get transactionAmountFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout[5];
	}

	/**
	 * Gets the auto allocate button configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public get autoAllocateButtonConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout[6];
	}

	/**
	 * Gets the allocation amount field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public get allocationAmountFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout[7];
	}

	/**
	 * Gets or sets the maximum amount allowed for the amount field.
	 *
	 * @type {number}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	private maximumAmount: number;

	/**
	 * Subject for managing subscription cleanup on component destruction.
	 *
	 * @private
	 * @type {Subject<void>}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	private readonly destroy: Subject<void> = new Subject<void>();

	/**
	 * Implements the on initialization interface.
	 *
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public ngOnInit(): void
	{
		this.initializeComponent();
	}

	/**
	 * Implements the on destroy interface to perform cleanup.
	 *
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public ngOnDestroy(): void
	{
		this.destroy.next();
		this.destroy.complete();
	}

	/**
	 * Performs component initialization.
	 *
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public initializeComponent(): void
	{
		this.currentData.reason =
			StringHelper.beforeCapitalSpaces(
				InsuranceConstants
					.commissionAdjustmentTypes
					.recordPayment);

		this.initializeLayout();

		this.context.source.addToNext(
			this.next.bind(this));

		this.context.source.wizardStepLoading = false;
	}

	/**
	 * Handles the navigation to the next step.
	 *
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public next(): void
	{
		const commissionTransaction: any =
			this.findCommissionTransaction(
				Number(this.currentData.commissionTransactionId));

		this.context.source.addOrUpdateStepData(
			{
				policyNumber:
					this.transaction
						.data
						.policyNumber,
				transactionId:
					this.transaction.id,
				note:
					this.getCommissionTransactionDescription(
						commissionTransaction)
			});
	}

	/**
	 * Validates the transaction number control.
	 *
	 * @param {AbstractControl} control
	 * The form control to get the input value.
	 * @return {Observable<boolean>}
	 * An Observable that resolves to a boolean value
	 * that indicates the transaction number validity.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public transactionNumberValidator(
		control: AbstractControl): Observable<boolean>
	{
		if (AnyHelper.isNullOrWhitespace(control.value))
		{
			return of(false);
		}

		return of(control.value)
			.pipe(
				delay(
					AppConstants
						.time
						.threeQuarterSecond),
				switchMap((value: any) =>
					from(
						this.insuranceService
							.searchTransaction(
								this.agencyId,
								value))
				),
				map((matchingTransaction: IEntityInstance) =>
				{
					this.transaction = matchingTransaction;

					return !AnyHelper
						.isNull(
							matchingTransaction);
				}));
	}

	/**
	 * Validates the selected commission transaction.
	 *
	 * @param {AbstractControl} control
	 * The form control to get the input value.
	 * @return {boolean}
	 * The commission transaction validity.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public commissionTransactionAmountValidator(
		control: AbstractControl): boolean
	{
		if (AnyHelper.isNullOrWhitespace(control.value))
		{
			return false;
		}

		const commissionAmount: number =
			this.findCommissionTransaction(
				Number(control.value))
				?.data
				?.amount;

		return !AnyHelper.isNull(commissionAmount)
			&& commissionAmount > 0;
	}

	/**
	 * Clears the transaction related data from the current step
	 * and the wizard context.
	 *
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public clearTransactionData(): void
	{
		this.transaction = null;
		this.paymentAllocationSettings = null;

		this.reloadCommissionTransactions();
	}

	/**
	 * Reloads the commission transactions associated
	 * to the current transaction.
	 *
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public reloadCommissionTransactions(): void
	{
		this.commissionTransactionFieldConfig
			.formControl
			.setValue(null);
		this.commissionTransactionFieldConfig
			.formControl
			.updateValueAndValidity();

		if (this.commissionTransactionFieldConfig
			.props
			.initializeDataOptions)
		{
			this.commissionTransactionFieldConfig
				.props
				.initializeDataOptions();
		}
	}

	/**
	 * Loads the commission transactions associated to the current transaction.
	 *
	 * @async
	 * @return {Promise<any[]>}
	 * The promise containing the list of commission transactions.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public async loadCommissionTransactions(): Promise<any[]>
	{
		if (AnyHelper.isNull(this.transaction))
		{
			this.commissionTransactions = [];

			return [];
		}

		this.commissionTransactions =
			await this.insuranceService
				.getPayableCommissionsByTransactionId(
					this.agencyId,
					this.transaction.id);

		return this.commissionTransactions;
	}

	/**
	 * Handles the status change of the transaction number control.
	 *
	 * @param {string} status
	 * The status of the formly field.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public async handleTransactionNumberStatus(
		status: string): Promise<void>
	{
		if(status
			=== FormlyConstants
				.controlStatus
				.pending)
		{
			return;
		}

		if (status
			=== FormlyConstants
				.controlStatus
				.invalid)
		{
			this.clearTransactionData();

			return;
		}

		const product: IEntityInstance =
			await this.insuranceService
				.getProductByTransaction(
					this.transaction);

		this.paymentAllocationSettings =
			product?.data?.billing?.paymentAllocations;

		this.reloadCommissionTransactions();

		this.paymentAllocations.paymentAllocationSettings =
			this.paymentAllocationSettings;
		this.paymentAllocations.transaction =
			this.transaction;
	}

	/**
	 * Handles the commission transaction field change event.
	 *
	 * @param {FormlyFieldConfig} field
	 * The formly field configuration.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public onCommissionTransactionChange(
		field: FormlyFieldConfig): void
	{
		const commissionTransaction: any =
			this.findCommissionTransaction(
				Number(field.formControl.value));

		this.setMaximumAmount(
			commissionTransaction);
		this.paymentAllocations
			?.setCommissionTransaction(
				commissionTransaction);
		this.amountFieldConfig
			.formControl
			.updateValueAndValidity();
	}

	/**
	 * Handles the validity changed event for the dynamic formly component.
	 * This will update the validity of the wizard step for action buttons.
	 *
	 * @param {boolean} isValid
	 * The validity of the current displayed step data set.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public async validityChanged(
		isValid: boolean): Promise<void>
	{
		await this.context.source.validStepChanged(
			isValid
				&& !AnyHelper.isNull(
					this.currentData.commissionTransactionId)
				&& (!this.paymentAllocationsEnabled
					|| (this.paymentAllocations?.valid ?? false)));
	}

	/**
	 * Handles the validity changed event for the payment allocations component.
	 * This will update the validity of the wizard step for action buttons.
	 *
	 * @param {boolean} isValid
	 * The validity of the payment allocations component.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public async paymentAllocationsValidityChanged(
		isValid: boolean): Promise<void>
	{
		await this.context.source.validStepChanged(
			isValid
				&& this.dynamicFormlyLayout[0]
					.formControl
					.parent
					.valid);
	}

	/**
	 * Gets the description to be shown for a
	 * commission transaction in the dropdown.
	 *
	 * @param {any} item
	 * The commission transaction item.
	 * @return {string}
	 * The description of the commission transaction.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public getCommissionTransactionDescription(
		item: any): string
	{
		return item.data.note
			+ ' - '
			+ StringHelper.format(
				item.data.amount,
				AppConstants.formatTypes.currency);
	}

	/**
	 * Finds the commission transaction object
	 * from the list of commission transactions
	 * based on the given commission transaction id.
	 *
	 * @param {number} commissionTransactionId
	 * The commission transaction ID.
	 * @return {any}
	 * The commission transaction object.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public findCommissionTransaction(
		commissionTransactionId: number): any
	{
		return this.commissionTransactions
			?.find(
				(item: any) =>
					item.id === commissionTransactionId);
	}

	/**
	 * Sets the maximum amount allowed for the amount field.
	 *
	 * @param {any} commissionTransaction
	 * The commission transaction object.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	private setMaximumAmount(
		commissionTransaction: any): void
	{
		if (AnyHelper.isNull(
			commissionTransaction))
		{
			this.maximumAmount = null;

			return;
		}

		const commissionAmount: number =
			commissionTransaction
				?.data
				?.amount;

		if (AnyHelper.isNull(commissionAmount)
			|| commissionAmount <= 0)
		{
			this.maximumAmount = null;

			return;
		}

		this.maximumAmount =
			commissionAmount;
	}

	/**
	 * Creates the dynamic formly layout.
	 *
	 * @memberof CommissionPaymentTransactionComponent
	 */
	private initializeLayout(): void
	{
		const allocationFieldsExpressions: any =
			{
				hide: () =>
					!this.paymentAllocationsEnabled
			};

		this.dynamicFormlyLayout =
			<FormlyFieldConfig[]>
			[
				{
					key: 'reason',
					type: FormlyConstants.customControls.input,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						type: 'string',
						label: 'Reason',
						disabled: true
					}
				},
				{
					key: 'transactionNumber',
					type: FormlyConstants.customControls.input,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						type: 'string',
						label: 'Transaction',
						required: true,
						tooltip:
							'Transaction Id can be found in the Transaction '
								+ 'table displayed in the Policy Term Summary '
								+ 'page'
					},
					hooks: {
						onInit: (field) => {
							field
								.formControl
								.statusChanges
								.pipe(takeUntil(this.destroy))
								.subscribe(
									status => {
										this.handleTransactionNumberStatus(
											status);
									});
						},
					},
					asyncValidators: {
						transactionMustExist: {
							expression: (
								control: UntypedFormControl,
								_: any) =>
								this.transactionNumberValidator(control),
							message:
								'This transaction number does not exist.'
						}
					}
				},
				{
					key: 'commissionTransactionId',
					type: FormlyConstants.customControls.customDataSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Notes',
						required: true,
						placeholder: 'Select an Option',
						showClear: true,
						dataPromise:
							this.loadCommissionTransactions.bind(this),
						labelFunction:
							this.getCommissionTransactionDescription.bind(this),
						valueTemplate: '${item.id}',
						type: 'number',
						tooltip:
							'Select the commission transaction to pay. '
								+ 'Only commission transactions that have not '
								+ 'been paid before or that are not part of '
								+ 'an active Commission Disbursement '
								+ 'will be listed here.',
						change:
							this.onCommissionTransactionChange.bind(this),
						disableAutoSetValue: true
					},
					validators: {
						commissionAmountMustBeGreaterThanZero: {
							expression: (control: UntypedFormControl) =>
								this.commissionTransactionAmountValidator(
									control),
							message:
								'Only commission transactions with an amount '
									+ 'greater than $0 can be selected.'
						}
					}
				},
				{
					key: 'amount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Amount',
						useCurrency: true,
						required: true
					},
					validators: {
						amountMustBeGreaterThanZero: {
							expression: (control: UntypedFormControl) =>
								control.value > 0,
							message: 'Amount must be greater than 0.'
						},
						maxAmount: {
							expression: (control: UntypedFormControl) =>
								AnyHelper.isNull(this.maximumAmount)
									|| control.value <= this.maximumAmount,
							message:
								'Amount cannot exceed the commission '
									+ 'transaction amount.'
						}
					}
				},
				{
					key: 'transactionDate',
					type: FormlyConstants.customControls.customCalendar,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Bank Transaction Date',
						required: true
					}
				},
				{
					key: 'transactionAmount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Transaction Amount',
						useCurrency: true,
						disabled: true
					},
					expressions:
						allocationFieldsExpressions
				},
				{
					type: FormlyConstants.customControls.customOperationButton,
					wrappers: [
					],
					props: {
						label: 'Auto Allocate',
						disabled: true,
						hideLabel: true,
						gridColumns: 6
					},
					expressions:
						allocationFieldsExpressions
				},
				{
					key: 'allocationAmount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Allocation Amount',
						useCurrency: true,
						disabled: true
					},
					expressions:
						allocationFieldsExpressions
				}
			];
	}
}